'use client';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from '../ui/command';
import { cn } from '../../lib/utils';
import { format, isValid, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Check, ChevronsUpDown, PackageOpenIcon, Plus, PlusIcon, X } from 'lucide-react';
import { type DetailedHTMLProps, type HTMLAttributes, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { toast } from 'sonner';
import { IconButton } from '../Button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Checkbox } from '../ui/checkbox';

import { Form as FormBase, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField } from '../ui/form';
import { Input as InputBase } from '../ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { debounce } from 'lodash';
import { toOneMinuteToMidnightUtil } from '../../lib/date.utils';
import { Loader } from '../Loader';

type TOptions = {
  label: string;
  value: string;
  icon?: ReactNode;
};

export enum EInput {
  TEXT = 'text',
  EMAIL = 'email',
  PASSWORD = 'password',
  NUMBER = 'number',
  TEL = 'tel',
  CNPJ = 'cnpj',
  MASK = 'mask',
  FILE = 'file',
  SWITCH = 'switch',
  TEXTAREA = 'textarea',
  SELECT = 'select',
  SELECT_SEARCH = 'select_search',
  SELECT_SEARCH_MULTI = 'select_search_multi',
  SELECT_SEARCH_ASYNC = 'select_search_async',
  DATEPICKER = 'datepicker',
  DATERANGE = 'daterange',
  COLORPICKER = 'colorpicker',
  MULTIPLE_TEXT = 'multiple_text',
  OTP = 'otp',
  CHECKBOX = 'checkbox',
  CHECKBOX_GROUP = 'checkbox_group',
  RADIO_GROUP = 'radio_group',
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
}

export type TFormProps = {
  children: ReactNode;
  ctx: any;
  onSubmit?: (data: any) => void;
  className?: string;
};

export type TInputProps = Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'defaultValue'> & {
  type: EInput;
  name: string;
  label: string | ReactNode;
  placeholder?: string;
  description?: string | ReactNode;
  mask?: string;
  patternChar?: string;
  disabled?: boolean;
  defaultValue?: any;
  defaultChecked?: boolean;
  selectGroup?: string;
  options?: Array<{ value: any; label: string; icon?: ReactNode }>;
  loadOptions?: (searchText: string) => Promise<TOptions[] | []>;
  optionIconOrientation?: 'left' | 'right';
  inputIcon?: ReactNode;
  inputIconOrientation?: 'left' | 'right';
  multipleFiles?: boolean;
  accepts?: string;
  inputMultipleTextRules?: RegExp;
  numberOfMonths?: number;
  radioItems?: { value: string; text: string }[];
  checkboxItems?: { value: string; label: string }[];
  loading?: boolean;
  disabledDate?: any;
  maxLength?: number;
  minLength?: number;
  maxValue?: number;
  minValue?: number;
  popoverClassName?: string;
  decimalScale?: number;
  canCreateOption?: boolean;
  onSearch?: (term: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
};

type TInputHandlerProps = TInputProps & {
  field: any;
};

type TInputMaskProps = Omit<TInputProps, 'name' | 'type' | 'label'> & {
  field: any;
};

type TInputSwitchProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputCheckboxProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputCheckboxGroupProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputRadioGroupProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputSelectProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputSelectSearchProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputSelectSearchMulti = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputSelectSearchAsyncProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputDatePickerProps = Omit<TInputProps, 'name' | 'type' | 'label'> & {
  field: any;
};

type TInputDateRangeProps = Omit<TInputProps, 'name' | 'type' | 'label'> & {
  field: any;
};

type TInputMultipleTextProps = Omit<TInputProps, 'name' | 'type' | 'label'> & {
  field: any;
};

type TInputFileProps = Omit<TInputProps, 'type' | 'label'> & {
  field: any;
};

type TInputCurrencyProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
};

type TInputPercentageProps = Omit<TInputProps, 'type' | 'label' | 'name'> & {
  field: any;
  decimalScale: number;
};

function InputMask({ field, mask, placeholder, disabled }: TInputMaskProps) {
  const { error } = useFormField();

  return (
    <PatternFormat
      disabled={disabled}
      className={cn(error && 'border-2 border-red-500')}
      value={field.value}
      onChange={(e) => field.onChange(e.target.value)}
      placeholder={placeholder}
      format={mask!}
      customInput={InputBase}
    />
  );
}

function InputSwitch({ field, disabled, defaultChecked }: TInputSwitchProps) {
  const { value } = field;

  return (
    <div className="flex items-center space-x-2">
      <Switch
        className="data-[state=checked]:bg-checkbox-checked-background"
        defaultChecked={defaultChecked}
        disabled={disabled}
        id={field.name}
        checked={value}
        onCheckedChange={field.onChange}
      />
      <Label className="font-semibold" htmlFor={field.name}>
        {value ? 'Sim' : 'Não'}
      </Label>
    </div>
  );
}

function InputCheckbox({ field, disabled }: TInputCheckboxProps) {
  const { value } = field;

  return <Checkbox disabled={disabled} checked={field.value} onCheckedChange={field.onChange} />;
}

function InputCheckboxGroup({ field, disabled, checkboxItems }: TInputCheckboxGroupProps) {
  const { value } = field;

  return (
    <div className="mt-3 flex gap-4">
      {checkboxItems?.map((item, index) => {
        return (
          <FormItem key={item.value} className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                disabled={disabled}
                checked={field.value?.includes(item.value)}
                onCheckedChange={(checked) => {
                  return checked
                    ? field.onChange([...field.value, item.value])
                    : field.onChange(field.value?.filter((value: any) => value !== item.value));
                }}
              />
            </FormControl>
            <FormLabel className="cursor-pointer font-normal">{item.label}</FormLabel>
          </FormItem>
        );
      })}
    </div>
  );
}

function InputRadioGroup({ field, disabled, radioItems }: TInputRadioGroupProps) {
  const { value } = field;

  return (
    <RadioGroup onValueChange={field.onChange} defaultValue={value} value={value} disabled={disabled} className="flex flex-wrap gap-4">
      {radioItems?.map((item, index) => (
        <FormItem key={`${item.value}-${index}`} className="flex items-center space-x-2 space-y-0">
          <FormControl>
            <RadioGroupItem value={item.value} />
          </FormControl>
          <FormLabel className={cn('font-normal', disabled && 'cursor-not-allowed opacity-50')}>{item.text}</FormLabel>
        </FormItem>
      ))}
    </RadioGroup>
  );
}

function InputSelect({ field, disabled, selectGroup, placeholder, options, inputIcon, optionIconOrientation, loading }: TInputSelectProps) {
  // TODO: Implementar logica de carregar select com dados de API
  const { error } = useFormField();
  const { setValue } = useFormContext();

  return (
    <Select
      onValueChange={(value) => (value === 'none' ? field.onChange('') : field.onChange(value))}
      value={field.value}
      disabled={disabled || loading}>
      <SelectTrigger className={cn('relative w-full whitespace-nowrap', error && 'border-2 border-destructive', inputIcon && '!pl-10')}>
        <SelectValue placeholder={placeholder || 'Selecione uma opção'} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options && options?.length > 0 ? (
            <>
              <SelectLabel>{selectGroup || 'Opções'}</SelectLabel>
              <SelectItem value={'none'}>Nenhum(a)</SelectItem>
              {options?.map((option, index) => (
                <SelectItem key={`select-${field.name}-${option.value}-${index}`} value={option.value}>
                  <div className={cn(option.icon && 'flex items-center gap-2', optionIconOrientation === 'right' && 'flex-row-reverse')}>
                    {option.icon}
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </>
          ) : (
            <SelectLabel className="m-0 p-2 text-center">{selectGroup || 'Não há opções para selecionar'}</SelectLabel>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function InputSelectSearch({
  field,
  options,
  placeholder = 'Selecione',
  inputIcon,
  disabled,
  optionIconOrientation,
  popoverClassName,
}: TInputSelectSearchProps) {
  const { setValue } = useFormContext();
  const { error } = useFormField();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();

  useEffect(() => {
    triggerRef.current?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options ?? [];
    const lower = search.toLowerCase();
    return (options ?? []).filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, search]);

  return (
    <Popover open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setSearch(''); }} modal={false}>
      <PopoverTrigger asChild>
        <FormControl>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded="false"
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-label={placeholder}
            title={placeholder}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-input border border-input-default-static-border bg-input-default-static-background px-3 py-2 text-sm',
              !field.value && 'text-input-default-static-placeholder',
              disabled && 'cursor-not-allowed bg-input-default-disabled-background text-input-default-disabled-text opacity-50',
              error && 'border-2 border-destructive',
              inputIcon && '!pl-10',
            )}>
            {field.value ? options?.find((options) => options.value === field.value)?.label : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent id={listboxId} className={cn('p-0', popoverClassName)} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={'none'}
                onSelect={() => {
                  setValue(field.name, '', { shouldDirty: true });
                  setOpen(false);
                }}>
                Nenhum(a)
              </CommandItem>
              {filteredOptions.map((option, index) => (
                <CommandItem
                  value={option.label}
                  key={'select-search-' + option.value + index}
                  onSelect={() => {
                    setValue(field.name, option.value, { shouldDirty: true });
                    setOpen(false);
                  }}>
                  <Check className={cn('mr-2 h-4 w-4', option.value === field.value ? 'opacity-100' : 'opacity-0')} />
                  {option.label}
                  <CommandShortcut>{option.icon}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InputSelectSearchMulti({
  field,
  options,
  placeholder = 'Selecione',
  inputIcon,
  disabled,
  optionIconOrientation,
  popoverClassName,
}: TInputSelectSearchMulti) {
  const { setValue } = useFormContext();
  const { error } = useFormField();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();

  useEffect(() => {
    triggerRef.current?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }, [open]);

  function setFieldValues(checked: boolean, value: string) {
    console.log({ checked, value });
    let newValues = field.value || [];
    if (checked) {
      newValues = [...newValues, value];
    } else {
      newValues = newValues.filter((v: string) => v !== value);
      if (newValues.length === 0) newValues = null;
    }

    setValue(field.name, newValues, { shouldDirty: true });
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <FormControl>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded="false"
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-label={placeholder}
            title={placeholder}
            disabled={disabled}
            className={cn(
              'flex min-h-10 w-full items-center justify-between rounded-input border border-input-default-static-border bg-input-default-static-background px-3 py-2 text-sm',
              !field.value && 'text-input-default-static-placeholder',
              disabled && 'cursor-not-allowed bg-input-default-disabled-background text-input-default-disabled-text opacity-50',
              error && 'border-2 border-destructive',
              inputIcon && '!pl-10',
            )}>
            {field.value ? (
              <div className="flex flex-wrap gap-2">
                {field.value.map((value: string, index: number) => (
                  <Badge key={'s-s-m-badge-' + value + index} variant={'info'} className="flex gap-2 px-2 py-[4px]">
                    <span className="text-xs">{options!.find((opt) => opt.value === value)!.label}</span>
                  </Badge>
                ))}
              </div>
            ) : (
              placeholder
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent id={listboxId} className={cn('p-0', popoverClassName)} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandInput placeholder="Buscar..." />
          <CommandList>
            <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
            <CommandGroup>
              {options?.map((option, index) => (
                <CommandItem
                  value={option.label}
                  key={'select-search-multi-' + option.value + index}
                  onSelect={() => {
                    setFieldValues(Array.isArray(field.value) ? !field.value.includes(option.value) : true, option.value);
                  }}>
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      (Array.isArray(field.value) ? field.value.some((v: TOptions) => v === option.value) : false) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InputSelectSearchAsync({
  field,
  loadOptions,
  options: externalOptions,
  onSearch,
  hasMore,
  onLoadMore,
  isFetchingMore,
  loading,
  placeholder,
  disabled,
  inputIcon,
  popoverClassName,
  canCreateOption,
  optionIconOrientation,
}: TInputSelectSearchAsyncProps) {
  const { setError, setValue } = useFormContext();
  const { error } = useFormField();
  const [loadingInternal, setLoadingInternal] = useState(false);
  const [suggestions, setSuggestions] = useState<TOptions[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    triggerRef.current?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }, [open]);

  // Controlled mode: external options + onSearch callback
  const isControlledMode = !!onSearch;
  const displayOptions = isControlledMode ? (externalOptions ?? []) : suggestions;
  // In controlled mode, isLoading reflects the external loading prop (initial fetch).
  // After that, loading inside the list is handled by isFetchingMore.
  const isLoading = isControlledMode ? (loading ?? false) : loadingInternal;

  // Pull mode: loadOptions callback (legacy)
  const debouncedLoadOptions = useCallback(
    debounce(async (newValue: string) => {
      setLoadingInternal(true);
      try {
        setSearchTerm(newValue);
        const opts = await loadOptions!(newValue);
        if (!opts) return setSuggestions([]);
        setSuggestions(opts);
      } finally {
        setLoadingInternal(false);
      }
    }, 300),
    [],
  );

  // Controlled mode: debounced onSearch
  const debouncedOnSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      onSearch!(value);
    }, 300),
    [onSearch],
  );

  const handleValueChange = (value: string) => {
    if (isControlledMode) {
      debouncedOnSearch(value);
    } else {
      debouncedLoadOptions(value);
    }
  };

  // Infinite scroll: detect when user reaches bottom of the list
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || !onLoadMore || isFetchingMore) return;
    // Only trigger when user has actually scrolled (scrollTop > 0) and is near the bottom
    if (el.scrollTop > 0 && el.scrollHeight - el.scrollTop - el.clientHeight <= 60) {
      onLoadMore();
    }
  }, [hasMore, onLoadMore, isFetchingMore]);

  // Selected label for display (controlled mode stores value as string ID)
  const selectedLabel = isControlledMode
    ? displayOptions.find((o) => o.value === field.value)?.label
    : field.value?.label;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <FormControl>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded="false"
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-label={placeholder || 'Selecione uma opção'}
            title={placeholder || 'Selecione uma opção'}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-input border border-input-default-static-border bg-input-default-static-background px-3 py-2 text-sm',
              !field.value && 'text-input-default-static-placeholder',
              disabled && 'cursor-not-allowed bg-input-default-disabled-background text-input-default-disabled-text opacity-50',
              error && 'border-2 border-destructive',
              inputIcon && '!pl-10',
            )}>
            {selectedLabel || placeholder}
            {isLoading && !open ? (
              <Loader className="ml-2 h-4 w-4 shrink-0" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent id={listboxId} className={cn('p-0', popoverClassName)} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false}>
          <div className="relative">
            <CommandInput placeholder="Buscar..." onValueChange={handleValueChange} />
            {isLoading && <Loader className="absolute right-2 top-3" />}
          </div>
          <CommandList ref={listRef} onScroll={handleScroll}>
            <CommandEmpty>
              {isLoading ? (
                <Skeleton className="h-20" />
              ) : searchTerm ? (
                <div className="flex flex-col items-center text-center">
                  <PackageOpenIcon size={24} className="mb-2 text-typography" />
                  <strong className="text-typography">Nenhuma opção encontrada.</strong>
                  <span className="text-xs text-typography-secondary">Tente buscar por outro termo.</span>
                </div>
              ) : (
                <span className="text-typography-secondary">Busque por um termo para que a lista seja preenchida.</span>
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={'none'}
                onSelect={() => {
                  setValue(field.name, '', { shouldDirty: true });
                  setOpen(false);
                }}>
                Nenhum(a)
              </CommandItem>
              {displayOptions?.map((option, index) => (
                <CommandItem
                  value={option.label}
                  key={'select-search-async-' + option.value + index}
                  onSelect={() => {
                    if (isControlledMode) {
                      setValue(field.name, option.value, { shouldDirty: true });
                    } else {
                      setValue(field.name, option, { shouldDirty: true });
                    }
                    setOpen(false);
                  }}>
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      (isControlledMode ? option.value === field.value : option.value === field.value?.value)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  {option.label}
                  <CommandShortcut>{option.icon}</CommandShortcut>
                </CommandItem>
              ))}
              {displayOptions.length === 0 && canCreateOption && searchTerm && !isLoading && (
                <CommandItem
                  value={searchTerm}
                  key={'select-search-async-create-' + searchTerm}
                  onSelect={() => {
                    setValue(field.name, { value: searchTerm, label: searchTerm }, { shouldDirty: true });
                    setOpen(false);
                  }}>
                  <Check className={cn('mr-2 h-4 w-4', searchTerm === field.value?.value ? 'opacity-100' : 'opacity-0')} />
                  {searchTerm}
                  <CommandShortcut>
                    <PlusIcon size={16} />
                  </CommandShortcut>
                </CommandItem>
              )}
              {isFetchingMore && (
                <CommandItem value="loading-more" disabled>
                  <Loader className="mr-2" />
                  <span className="text-sm text-typography-secondary">Carregando mais...</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InputDatePicker({ field, disabled, placeholder, disabledDate = null }: TInputDatePickerProps) {
  const { error } = useFormField();

  return (
    <InputBase
      {...field}
      type={'date'}
      disabled={disabled}
      className={cn(error && 'border-2 border-red-500')}
      placeholder={placeholder}
      onChange={field.onChange}
    />
  );
}

// function InputDatePicker({ field, disabled, placeholder, disabledDate = null }: TInputDatePickerProps) {
//   const { error } = useFormField();
//   const inputId = useId();
//   const [month, setMonth] = useState(new Date());
//   const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
//   const [inputValue, setInputValue] = useState('');

//   useEffect(() => {
//     if (field.value) {
//       const date = toOneMinuteToMidnightUtil(field.value);
//       if (isValid(date) && !selectedDate) {
//         console.log('Setando Date Picker', date);
//         setSelectedDate(date);
//         setMonth(date);
//         setInputValue(format(date, 'dd/MM/yyyy'));
//       }
//     }
//   }, [field.value]);

//   const handleDayPickerSelect = (date: Date | undefined) => {
//     if (!date) {
//       setInputValue('');
//       setSelectedDate(undefined);
//     } else {
//       const dateToSave = toOneMinuteToMidnightUtil(date);
//       setSelectedDate(dateToSave);
//       setMonth(dateToSave);
//       setInputValue(format(dateToSave, 'dd/MM/yyyy'));
//       // field.onChange(format(date, 'dd/MM/yyyy'));
//       field.onChange(dateToSave);
//     }
//   };

//   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     setInputValue(e.target.value);
//     const value = e.target.value.replace(/\s+/g, '');

//     const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

//     if (isValid(parsedDate) && value.length === 10) {
//       const dateToSave = toOneMinuteToMidnightUtil(parsedDate);
//       setSelectedDate(dateToSave);
//       setMonth(dateToSave);
//       field.onChange(dateToSave);
//     } else {
//       setSelectedDate(undefined);
//       field.onChange('');
//     }
//   };

//   return (
//     <Popover>
//       <div className="relative">
//         <PatternFormat
//           disabled={disabled}
//           className={cn(error && 'border-2 border-red-500')}
//           id={inputId}
//           value={inputValue}
//           onChange={handleInputChange}
//           placeholder={placeholder}
//           format={'##/##/####'}
//           customInput={InputBase}
//         />
//         <PopoverTrigger asChild className={cn('absolute right-3 top-[12px]', disabled && 'pointer-events-none opacity-70')} disabled={disabled}>
//           <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
//         </PopoverTrigger>
//       </div>
//       <PopoverContent className="w-auto p-0" align="start">
//         <Calendar
//           disabled={disabledDate}
//           month={month}
//           onMonthChange={setMonth}
//           mode="single"
//           selected={selectedDate}
//           onSelect={handleDayPickerSelect}
//           captionLayout="dropdown"
//           hideNavigation
//         />
//       </PopoverContent>
//     </Popover>
//   );
// }

function InputDateRange({ field, numberOfMonths = 2 }: TInputDateRangeProps) {
  const { error } = useFormField();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Selecionar período"
          title="Selecionar período"
          className={cn(
            'flex h-10 w-full rounded-md border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-checkbox-static-border disabled:cursor-not-allowed disabled:opacity-50',
            !field.value && 'text-muted-foreground',
            error && 'border-2 border-input-invalid-static-border',
          )}>
          {field.value?.from ? (
            field.value.to ? (
              <>
                {format(field.value.from, 'PPP', { locale: ptBR })} - {format(field.value.to, 'PPP', { locale: ptBR })}
              </>
            ) : (
              format(field.value.from, 'PPP', { locale: ptBR })
            )
          ) : (
            <span>Pick a date</span>
          )}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {/* <Calendar
          autoFocus
          mode="range"
          locale={ptBR}
          defaultMonth={field.value?.from || new Date()}
          disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
          selected={field.value}
          onSelect={field.onChange}
          numberOfMonths={2}
        /> */}
      </PopoverContent>
    </Popover>
  );
}

function InputMultipleText({ field, disabled, placeholder, inputMultipleTextRules }: TInputMultipleTextProps) {
  const { error } = useFormField();
  const { getValues, setValue, watch } = useFormContext();
  const ghostInputRef = useRef<HTMLInputElement>(null);
  const fieldWatch = watch(field.name, '');

  function addItem() {
    if (!ghostInputRef.current?.value)
      toast.error('Erro ao adicionar valor', {
        description: 'O campo não pode estar vazio. Insira um valor válido.',
      });

    console.log({ inputMultipleTextRules });
    if (!new RegExp(inputMultipleTextRules || '').test(ghostInputRef.current!.value))
      return toast.error('Erro ao adicionar valor', {
        description: 'Insira um valor válido.',
      });
    const newValue = field.value ? `${field.value},${ghostInputRef.current?.value}` : ghostInputRef.current?.value;

    setValue(field.name, newValue, { shouldDirty: true });
    ghostInputRef.current!.value = '';
  }

  return (
    <>
      <div className="relative">
        <InputBase {...field} disabled={disabled} className={'hidden'} placeholder={placeholder} type={'text'} />
        <InputBase ref={ghostInputRef} disabled={disabled} className={cn(error && 'border-invalid')} placeholder={placeholder} type={'text'} />
        <IconButton
          variant={'ghost'}
          type="button"
          aria-label="Adicionar item"
          title="Adicionar item"
          disabled={disabled}
          onClick={addItem}
          className="absolute right-1 top-[3px]">
          <Plus size={18} />
        </IconButton>
      </div>
      <div className="flex flex-wrap">
        {fieldWatch &&
          fieldWatch.split(',').map((item: string, index: number) => (
            <Badge key={item + '-' + index} className="mr-2 mt-1 flex gap-2 pl-4 pr-2">
              <span>{item}</span>
              <IconButton
                aria-label={`Remover ${item}`}
                title={`Remover ${item}`}
                onClick={() => {
                  const newValue = fieldWatch.split(',').filter((_: any, i: any) => i !== index);
                  field.onChange(newValue.join(','));
                }}
                variant={'ghost'}>
                <X size={12} />
              </IconButton>
            </Badge>
          ))}
      </div>
    </>
  );
}

function InputFile({ field, disabled, placeholder, multipleFiles, id, accepts = 'image/*,video/*,.docx,.xlsx,.csv,.pdf' }: TInputFileProps) {
  const { error } = useFormField();
  const formContext = useFormContext();

  //workaround pra fazer funcionar o input file com react-hook-form e shadcn/ui
  const inputRef = formContext.register(field.name);
  return (
    <InputBase
      {...inputRef}
      id={id}
      multiple={multipleFiles}
      accept={accepts}
      disabled={disabled}
      className={cn(error && 'border-2 border-input-invalid-static-border')}
      placeholder={placeholder}
      onChange={(event) => {
        if (multipleFiles) field.onChange(event.target?.files ?? undefined);
        else field.onChange(event.target?.files?.[0] ?? undefined);
      }}
      type={'file'}
    />
  );
}

function InputCurrency({ field, disabled, placeholder, minValue, maxValue }: TInputCurrencyProps) {
  const { error } = useFormField();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleValueChange = (values: any) => {
    const { floatValue } = values;
    field.onChange(floatValue);
    
    // Garante que o cursor fique no final após a formatação
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;
    const numericValue = value.replace(/[^\d]/g, '');

    // Permite teclas de navegação e controle - não interfere
    if (['ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) {
      return;
    }

    // Se o usuário pressionar uma tecla numérica
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();

      // Adiciona o novo dígito no final
      const newNumericValue = numericValue + e.key;

      // Converte para valor decimal (divide por 100 para centavos)
      const decimalValue = parseFloat(newNumericValue) / 100;

      // Atualiza o campo
      field.onChange(decimalValue);

      // Posiciona o cursor no final após a atualização
      requestAnimationFrame(() => {
        input.setSelectionRange(input.value.length, input.value.length);
      });
      return;
    }

    // Trata Backspace e Delete - remove do final
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();

      if (numericValue.length > 0) {
        // Remove o último dígito
        const newNumericValue = numericValue.slice(0, -1);
        
        if (newNumericValue.length === 0) {
          field.onChange(0);
        } else {
          // Converte para valor decimal (divide por 100 para centavos)
          const decimalValue = parseFloat(newNumericValue) / 100;
          field.onChange(decimalValue);
        }

        // Posiciona o cursor no final após a atualização
        requestAnimationFrame(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }
      return;
    }

    // Bloqueia outras teclas que não sejam numéricas ou de controle
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
    }
  };

  return (
    <NumericFormat
      getInputRef={(el: HTMLInputElement | null) => {
        inputRef.current = el;
      }}
      disabled={disabled}
      className={cn(error && 'border-2 border-red-500')}
      value={field.value}
      onValueChange={handleValueChange}
      placeholder={placeholder || 'R$ 0,00'}
      decimalSeparator=","
      thousandSeparator="."
      prefix="R$"
      decimalScale={2}
      fixedDecimalScale={true}
      allowLeadingZeros={false}
      onKeyDown={handleKeyDown}
      onFocus={(e) => {
        // Move o cursor para o final quando focar
        requestAnimationFrame(() => {
          const input = e.target;
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }}
      isAllowed={(values) => {
        const { floatValue } = values;
        if (floatValue === undefined) return true;
        if (minValue && maxValue) {
          return floatValue <= maxValue && floatValue >= minValue;
        }
        if (minValue) {
          return floatValue >= minValue;
        }
        if (maxValue) {
          return floatValue <= maxValue;
        }
        return Boolean(floatValue);
      }}
      customInput={InputBase}
    />
  );
}

function InputPercentage({ field, disabled, placeholder, maxValue, minValue }: TInputPercentageProps) {
  const { error } = useFormField();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleValueChange = (values: any) => {
    const { floatValue } = values;
    field.onChange(floatValue);

    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;
    const numericValue = value.replace(/[^\d]/g, '');

    if (['ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) {
      return;
    }

    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      const newNumericValue = numericValue + e.key;
      const decimalValue = parseFloat(newNumericValue) / 100;
      field.onChange(decimalValue);

      requestAnimationFrame(() => {
        input.setSelectionRange(input.value.length, input.value.length);
      });
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (numericValue.length > 0) {
        const newNumericValue = numericValue.slice(0, -1);
        if (newNumericValue.length === 0) {
          field.onChange(0);
        } else {
          const decimalValue = parseFloat(newNumericValue) / 100;
          field.onChange(decimalValue);
        }
        requestAnimationFrame(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }
      return;
    }

    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
    }
  };

  return (
    <NumericFormat
      getInputRef={(el: HTMLInputElement) => {
        inputRef.current = el;
      }}
      disabled={disabled}
      className={cn(error && 'border-2 border-red-500')}
      value={field.value}
      onValueChange={handleValueChange}
      placeholder={placeholder || '0,00%'}
      decimalSeparator=","
      thousandSeparator="."
      suffix="%"
      decimalScale={2}
      fixedDecimalScale={true}
      allowLeadingZeros={false}
      onKeyDown={handleKeyDown}
      onFocus={(e) => {
        requestAnimationFrame(() => {
          const input = e.target;
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }}
      isAllowed={(values) => {
        const { floatValue } = values;
        if (floatValue === undefined) return true;
        if (minValue && maxValue) {
          return floatValue <= maxValue && floatValue >= minValue;
        }
        if (minValue) {
          return floatValue >= minValue;
        }
        if (maxValue) {
          return floatValue <= maxValue;
        }
        return true;
      }}
      customInput={InputBase}
    />
  );
}

function InputHanlder({
  type,
  field,
  placeholder,
  mask,
  disabled,
  options,
  loadOptions,
  optionIconOrientation,
  inputIcon,
  multipleFiles,
  inputMultipleTextRules,
  accepts,
  id,
  numberOfMonths,
  loading,
  radioItems,
  checkboxItems,
  disabledDate,
  maxLength,
  minLength,
  minValue,
  maxValue,
  decimalScale = 2,
  defaultValue,
  popoverClassName,
  defaultChecked,
  canCreateOption,
  onSearch,
  hasMore,
  onLoadMore,
  isFetchingMore,
}: TInputHandlerProps): ReactNode {
  const { error, formItemId } = useFormField();
  switch (type) {
    case EInput.MASK:
      return <InputMask field={field} mask={mask} disabled={disabled} placeholder={placeholder} />;

    case EInput.SWITCH:
      return <InputSwitch field={field} disabled={disabled} defaultChecked={defaultChecked} />;

    case EInput.SELECT:
      return (
        <InputSelect
          field={field}
          disabled={disabled}
          options={options}
          loading={loading}
          placeholder={placeholder}
          inputIcon={inputIcon}
          popoverClassName={popoverClassName}
          optionIconOrientation={optionIconOrientation}
        />
      );

    case EInput.SELECT_SEARCH:
      return (
        <InputSelectSearch
          field={field}
          disabled={disabled}
          options={options}
          loading={loading}
          inputIcon={inputIcon}
          popoverClassName={popoverClassName}
          optionIconOrientation={optionIconOrientation}
        />
      );

    case EInput.SELECT_SEARCH_MULTI:
      return (
        <InputSelectSearchMulti
          field={field}
          disabled={disabled}
          options={options}
          loading={loading}
          inputIcon={inputIcon}
          popoverClassName={popoverClassName}
          optionIconOrientation={optionIconOrientation}
        />
      );

    case EInput.SELECT_SEARCH_ASYNC:
      return (
        <InputSelectSearchAsync
          field={field}
          disabled={disabled}
          loadOptions={loadOptions}
          options={options}
          onSearch={onSearch}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          isFetchingMore={isFetchingMore}
          loading={loading}
          inputIcon={inputIcon}
          placeholder={placeholder}
          optionIconOrientation={optionIconOrientation}
          popoverClassName={popoverClassName}
          canCreateOption={canCreateOption}
        />
      );

    case EInput.TEXTAREA:
      return (
        <Textarea {...field} disabled={disabled} className={cn(error && 'border-2 border-red-500')} placeholder={placeholder || 'Digite aqui...'} />
      );

    case EInput.DATEPICKER:
      return <InputDatePicker field={field} placeholder={placeholder || 'Selecione ou digite'} disabledDate={disabledDate} disabled={disabled} />;

    case EInput.DATERANGE:
      return <InputDateRange field={field} numberOfMonths={numberOfMonths} placeholder={placeholder || 'Selecione ou digite'} />;

    case EInput.MULTIPLE_TEXT:
      return <InputMultipleText field={field} disabled={disabled} placeholder={placeholder} inputMultipleTextRules={inputMultipleTextRules} />;

    case EInput.FILE:
      return <InputFile field={field} disabled={disabled} name={field.name} multipleFiles={multipleFiles} id={id} accepts={accepts} />;

    case EInput.OTP:
      return (
        <InputOTP maxLength={6} disabled={disabled} {...field}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      );

    case EInput.CHECKBOX:
      return <InputCheckbox field={field} disabled={disabled} defaultChecked={defaultChecked} />;

    case EInput.CHECKBOX_GROUP:
      return <InputCheckboxGroup field={field} disabled={disabled} checkboxItems={checkboxItems} />;

    case EInput.RADIO_GROUP:
      return <InputRadioGroup field={field} disabled={disabled} radioItems={radioItems} />;

    case EInput.CURRENCY:
      return <InputCurrency field={field} disabled={disabled} placeholder={placeholder} minValue={minValue} maxValue={maxValue} />;

    case EInput.PERCENTAGE:
      return (
        <InputPercentage
          field={field}
          disabled={disabled}
          placeholder={placeholder}
          minValue={minValue}
          maxValue={maxValue}
          decimalScale={decimalScale}
        />
      );

    default:
      return (
        <InputBase
          {...field}
          disabled={disabled}
          className={cn(error && 'border-2 border-red-500')}
          placeholder={placeholder}
          type={type}
          maxLength={maxLength}
          minLength={minLength}
          min={minValue}
          max={maxValue}
        />
      );
  }
}

export function Input(props: TInputProps) {
  const { label, name, description, loading, type } = props;
  const rhfInstance = useFormContext();
  const { error } = useFormField();

  // SELECT_SEARCH_ASYNC manages its own loading state internally;
  // using the outer Skeleton would unmount the Popover and close the dropdown.
  const showSkeleton = loading && type !== EInput.SELECT_SEARCH_ASYNC;

  return (
    <FormField
      control={rhfInstance.control}
      name={name}
      defaultValue={props.defaultValue ? props.defaultValue : props.type === EInput.SWITCH || props.type === EInput.CHECKBOX ? false : ''}
      render={({ field }) => {
        return (
          <FormItem className="relative mb-4 w-full">
            <div className={cn('w-full space-y-1', props.className)}>
              <FormLabel htmlFor={props.id}>{label}</FormLabel>
              <FormControl>
                {showSkeleton ? (
                  <Skeleton className="flex h-10 w-full rounded-md border-input bg-gray-400 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:!border-2 focus-visible:!border-checkbox-static-border focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50" />
                ) : props.inputIcon ? (
                  <div className={cn('relative', props.inputIcon && props.inputIconOrientation !== 'right' && '[&>input]:!pl-10')}>
                    <InputHanlder field={field} {...props} />
                    {props.inputIcon && (
                      <div
                        className={cn(
                          'absolute left-3 top-1/2 -translate-y-1/2 transform',
                          props.inputIconOrientation === 'right' && 'left-[unset] right-3',
                          error && '[&>svg]:!bg-input-invalid-static-text',
                        )}>
                        {props.inputIcon}
                      </div>
                    )}
                  </div>
                ) : (
                  <InputHanlder field={field} {...props} />
                )}
              </FormControl>
              {description && !showSkeleton && <FormDescription className="text-xs">{description}</FormDescription>}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export function Form({ children, ctx, className, onSubmit }: TFormProps) {
  return (
    <FormBase {...ctx}>
      <form onSubmit={ctx.handleSubmit(onSubmit)} className={className}>
        {children}
      </form>
    </FormBase>
  );
}
